/**
 * `<page-generation-yaml>` — the rendered bundle of one generation, routed at
 * `/instances/:id/generations/:gen/yaml`.
 *
 * Implements `SC-F602` (the read-only YAML view: every file at the path it takes in
 * the policy directory, plus the rendered `server.yaml` as a separate artifact,
 * with line numbers, highlighting and copy — and **no editing affordance at all**,
 * not a disabled textarea and not a hidden save), `SC-F613` (the
 * control-plane-owned half of `server.yaml` is visible read-only, so a customer can
 * see what we render without being offered a field they may not set), `SC-F619`
 * (the same content as one archive) and `SC-F608` (the digest displayed next to the
 * bundle, with the honest note that the text shown is redacted and therefore does
 * **not** hash to it).
 *
 * Stories: `SC-S602`, `SC-S612`, `SC-S614`.
 *
 * This is the answer to requirement 6b — "users must not hand-write YAML, but must
 * be able to view it" — and it is the same YAML a self-hosted Sluice consumes:
 * `sluice serve --policies-dir policies.d --config server.yaml`. Structured records
 * are the source of truth; this pane is presentational, and there is no code path
 * anywhere in the product that accepts YAML text as an object's state.
 */

import { LitElement, css, html, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../components/index.js";
import { toast, type BnKvRow, type BnSelectChangeDetail, type BnSelectOption } from "../components/index.js";

import { ApiError, parseError } from "../api/errors.js";
import {
  bundleFilename,
  downloadGenerationBundle,
  getGeneration,
  getGenerationYaml,
  GENERATION_STATE_LABELS,
  type Generation,
  type GenerationYaml,
} from "../api/generations.js";
import { getInstance, type Instance } from "../api/instances.js";
import { hrefFor } from "../nav.js";

/** SERVER_CONFIG_PATH is the rendered server configuration's path inside the
 * bundle. It is called out separately everywhere it appears, because it is the one
 * artifact whose change is restart-class and the one whose text is redacted. */
const SERVER_CONFIG_PATH = "server.yaml";

@customElement("page-generation-yaml")
export class PageGenerationYaml extends LitElement {
  /** The instance record id, from the route. */
  @property() instanceId = "";
  /** The generation number, from the route. It arrives as a string. */
  @property() generation = "";

  @state() private view: GenerationYaml | null = null;
  @state() private gen: Generation | null = null;
  @state() private instance: Instance | null = null;
  @state() private loading = true;
  @state() private failure: ApiError | null = null;

  /** Which file is showing. One editor at a time: twelve CodeMirror instances on
   * one screen is a lot of DOM for a reader who is looking at one file. */
  @state() private selected = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .back {
      display: inline-block;
      margin-bottom: var(--bino-space-md);
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      text-decoration: none;
      border-radius: var(--bino-radius-sm);
    }
    .back:hover {
      color: var(--bino-text);
    }
    .back:focus-visible {
      outline: none;
      box-shadow: var(--bino-focus-ring-shadow);
    }
    header {
      display: flex;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: var(--bino-space-md);
      margin-bottom: var(--bino-space-lg);
    }
    .head-text {
      flex: 1 1 24rem;
      min-width: 0;
    }
    h1 {
      margin: 0;
      font-size: var(--bino-font-size-xl);
      font-weight: 600;
    }
    .lede {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
      max-width: 72ch;
    }
    .head-actions {
      display: flex;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      align-items: center;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-lg);
    }
    p.copy {
      margin: 0 0 var(--bino-space-sm);
      max-width: 72ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
    p.copy:last-child {
      margin-bottom: 0;
    }
    p.copy strong {
      color: var(--bino-text);
    }
    code {
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-base);
    }
    .picker {
      display: flex;
      align-items: flex-end;
      gap: var(--bino-space-sm);
      flex-wrap: wrap;
      margin-bottom: var(--bino-space-sm);
    }
    .picker bn-field {
      flex: 1 1 22rem;
      margin-bottom: 0;
    }
    /* The code surface gets a fixed height so a 900-line bundle scrolls inside its
       own container and the page body never scrolls horizontally (SC-S602). */
    .code {
      height: 32rem;
    }
    .meta {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
    }
    .failure {
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-md);
    }
    .failure p {
      margin: 0;
      max-width: 70ch;
      font-size: var(--bino-font-size-md);
      color: var(--bino-text-secondary);
    }
  `;

  /** Driven from `willUpdate` alone: the router sets both properties before the
   * first update, so loading from `connectedCallback` too would render the bundle
   * twice. */
  override willUpdate(changed: PropertyValues<this>) {
    if ((changed.has("instanceId") || changed.has("generation")) && this.instanceId && this.generation) {
      void this.load();
    }
  }

  private get number(): number {
    return Number.parseInt(this.generation, 10);
  }

  private async load() {
    this.loading = true;
    try {
      const [view, gen, instance] = await Promise.all([
        getGenerationYaml(this.instanceId, this.number),
        getGeneration(this.instanceId, this.number).catch(() => null),
        getInstance(this.instanceId).catch(() => null),
      ]);
      this.view = view;
      this.gen = gen;
      this.instance = instance;
      this.failure = null;
      this.selected = view.files[0]?.path ?? "";
    } catch (err) {
      this.failure = parseError(err);
      this.view = null;
    } finally {
      this.loading = false;
    }
  }

  private async download() {
    const view = this.view;
    if (!view) return;
    try {
      const blob = await downloadGenerationBundle(this.instanceId, view.generation);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = bundleFilename(this.instance?.name ?? "instance", view.generation);
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (err) {
      toast(parseError(err).message, "error");
    }
  }

  /** copyAll copies the whole bundle as one document, with each file preceded by a
   * comment naming its path — which is what a reader pasting into a ticket wants,
   * and what `sluice serve` would find if the comment lines were split back out. */
  private async copyAll() {
    const view = this.view;
    if (!view) return;
    const text = view.files.map((f) => `# ${f.path}\n${f.content}`).join("\n---\n");
    try {
      await navigator.clipboard.writeText(text);
      toast("The whole bundle is on the clipboard.", "success");
    } catch {
      toast("The clipboard is not available in this browser context.", "warning");
    }
  }

  private supportRows(err: ApiError): BnKvRow[] {
    const rows: BnKvRow[] = [{ label: "Error code", value: err.code }];
    if (err.requestId) rows.push({ label: "Request id", value: err.requestId });
    return rows;
  }

  private fileOptions(view: GenerationYaml): BnSelectOption[] {
    return view.files.map((f) => ({
      value: f.path,
      label: f.path,
      description:
        f.path === SERVER_CONFIG_PATH
          ? "The rendered server configuration — a separate artifact from the policy bundle, and the one whose change is restart-class."
          : `${f.content.split("\n").length} lines`,
    }));
  }

  override render() {
    if (this.loading) {
      return html`<bn-spinner block size="28" label="Rendering the bundle"></bn-spinner>`;
    }
    if (this.failure) {
      return html`<a class="back" href=${hrefFor("instances", this.instanceId, "generations", this.generation)}
          >← Generation ${this.generation}</a
        >
        <bn-card heading="Could not render this generation">
          <div class="failure" role="alert">
            <p>${this.failure.message}</p>
            <bn-kv .rows=${this.supportRows(this.failure)}></bn-kv>
            <div>
              <bn-button variant="primary" @click=${() => void this.load()}>Try again</bn-button>
            </div>
          </div>
        </bn-card>`;
    }
    const view = this.view;
    if (!view) return nothing;
    const file = view.files.find((f) => f.path === this.selected) ?? view.files[0];
    const isServerConfig = file?.path === SERVER_CONFIG_PATH;
    return html`
      <a class="back" href=${hrefFor("instances", this.instanceId, "generations", this.generation)}>
        ← Generation ${this.generation}
      </a>
      <header>
        <div class="head-text">
          <h1>Generation ${view.generation} — rendered YAML</h1>
          <p class="lede">
            This is the bundle exactly as it is mounted into the instance's policy directory, and it
            is the same YAML a self-hosted Sluice consumes:
            <code>sluice serve --policies-dir policies.d --config server.yaml</code>. It is
            <strong>read-only by construction</strong> — no surface in this product accepts YAML text
            as an object's state, so there is nothing here to save.
          </p>
        </div>
        <div class="head-actions">
          ${this.gen
            ? html`<bn-badge variant="neutral">
                ${GENERATION_STATE_LABELS[this.gen.state] ?? this.gen.state}
              </bn-badge>`
            : nothing}
          <bn-button @click=${() => void this.copyAll()}>Copy whole bundle</bn-button>
          <bn-button variant="primary" @click=${() => void this.download()}>
            Download archive
          </bn-button>
        </div>
      </header>

      <div class="stack">
        <bn-card heading="Digest">
          <bn-kv
            .rows=${[
              { label: "Bundle digest", value: view.rendered_digest },
              { label: "server.yaml hash", value: view.server_hash },
            ] as BnKvRow[]}
          ></bn-kv>
          <p class="copy" style="margin-top: var(--bino-space-sm)">
            <strong>${view.redaction_note}</strong>
          </p>
          <p class="copy">
            The digest is computed over the real bytes on purpose. Hashing the redacted text instead
            would break the one thing the digest exists for: the publish confirmation that compares it
            against what the instance reports.
          </p>
        </bn-card>

        <bn-card heading="Files">
          <div class="picker">
            <bn-field label="File" hint="Paths are exactly as they appear in the policy directory.">
              <bn-select
                .options=${this.fileOptions(view)}
                .value=${this.selected}
                filterable
                @change=${(e: CustomEvent<BnSelectChangeDetail>) => (this.selected = e.detail.value)}
              ></bn-select>
            </bn-field>
            <span class="meta">${view.files.length} file(s) in this generation</span>
          </div>
          ${isServerConfig
            ? html`<p class="copy">
                The rendered server configuration is a separate artifact from the policy bundle. Every
                control-plane-owned field in it — the listeners, the admin token, the audit
                destinations, the genesis seed, the API-key pepper,
                <code>policies.directory</code> and <code>policies.reload</code> — is set by Sluice
                Cloud and cannot be edited: submitting one is refused by name rather than silently
                dropped. <code>admin.token</code> is the one value replaced by a placeholder here,
                because this view is readable by every member of the organization.
              </p>`
            : nothing}
          ${file
            ? html`<bn-code
                class="code"
                language="yaml"
                .value=${file.content}
                filename=${file.path}
                label=${file.path}
              ></bn-code>`
            : html`<p class="copy">
                This generation rendered no files, which should be impossible — a bundle with zero
                policies still renders a comment.
              </p>`}
        </bn-card>

        <bn-panel eyebrow="What you can do with this archive">
          <p class="copy">
            The download contains <code>policies.d/</code> and <code>server.yaml</code> exactly as
            rendered. Two honest caveats travel with it: the secret references point at
            <code>/run/secrets/…</code> paths that exist only inside a Sluice Cloud container, so a
            self-hosted deployment must provide equivalents; and the control-plane-owned server
            settings carry placeholder values, clearly marked, because exporting the real ones would
            export credentials.
          </p>
        </bn-panel>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "page-generation-yaml": PageGenerationYaml;
  }
}
